import { Theme } from '@mui/material';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';
import moment from 'moment';

import { IESSAvailabilityCycleDTO } from 'src/api/dtos';
import { API_DATE_FORMAT, DAY_FULL_FORMAT } from 'src/shared/constants';
import { AvailabilityStatusEnums, CycleTypeEnums, OperationTypeEnums } from 'src/shared/enums';
import { TranslationFunction } from 'src/shared/providers';
import { getTimeFromSeconds } from 'src/shared/utils';

import { AvailabilityCycleInputsType, PreferenceInputsType } from './availabilities.types';

export const mapAvailabilityStatusEnum = (status: AvailabilityStatusEnums, t: TranslationFunction) => {
  switch (status) {
    case AvailabilityStatusEnums.AVAILABLE:
      return t('availability.available');
    case AvailabilityStatusEnums.NOT_AVAILABLE:
      return t('availability.unavailable');
    case AvailabilityStatusEnums.INDIFFERENT:
    default:
      return t('availability.indifferent');
  }
};

export const mapCycleTypeEnum = (status: CycleTypeEnums, t: TranslationFunction) => {
  switch (status) {
    case CycleTypeEnums.BI_WEEKLY:
      return t('availability.biWeekly');
    case CycleTypeEnums.WEEKLY:
      return t('availability.weekly');
    case CycleTypeEnums.DAYS:
    default:
      return t('availability.days');
  }
};

/**
 * Preferences (availabilities) from cycles are returned as a flat list.
 * However, the UI must show them nested by periods (daily, weekly or bi-weekly)
 * and by date/dayOfCycle.
 *
 * To facilitate the display and editing of availabilities,
 * the flast list is converted into a nested object before use.
 *
 * Example: {
 *  ...,
 *  groups: {
 *    6-2024: {
 *      1: [preference, preference],
 *      2: [preference],
 *      ...
 *    },
 *    7-2024: {
 *      8: [preferences],
 *      9: [preferences],
 *      ...
 *    },
 *    ...
 *  }
 */

export const buildEditCycleInputs = (cycle: IESSAvailabilityCycleDTO): AvailabilityCycleInputsType => {
  const { id, employeeId, cycleStart, validFrom, cycleLength, type, preferences, isLocked } = cycle;

  const orderedPreferenceInputsByDate = orderBy(preferences, ['date'], ['asc']).map(
    ({ id, date, dayOfCycle, status, startTimeInSeconds, endTimeInSeconds }) => ({
      date,
      dayOfCycle,
      operationType: !isNil(id) ? OperationTypeEnums.UPDATE : OperationTypeEnums.ADD,
      status: !isNil(id) ? status : AvailabilityStatusEnums.NOT_AVAILABLE, // When adding a new preference (from Not specified) default status should be not available
      id: id ?? undefined,
      startTimeInSeconds: startTimeInSeconds ?? undefined,
      endTimeInSeconds: endTimeInSeconds ?? undefined,
    }),
  );

  const firstDayOfWeek = orderedPreferenceInputsByDate[0]?.date;

  const preferencesByPeriod = orderedPreferenceInputsByDate.reduce<Record<string, PreferenceInputsType[]>>(
    (acc, dto) => {
      const offsetFromFirstDayOfWeek = moment(dto.date).diff(firstDayOfWeek, 'days');

      const weekKey = Math.floor(offsetFromFirstDayOfWeek / 7);

      const periodKey: string =
        cycle.type === CycleTypeEnums.DAYS
          ? 'day' // create static key: 'day'
          : `${weekKey}`; // create a composed key: 'week-year'

      const currentPreferences = acc[periodKey];
      const newPreferences = !currentPreferences ? [dto] : [...currentPreferences, dto];

      return { ...acc, [periodKey]: newPreferences };
    },
    {},
  );

  const preferencesByPeriodAndDate = Object.entries(preferencesByPeriod).reduce(
    (acc, [periodKey, preferencesByPeriodList]) => {
      const preferencesByDate = preferencesByPeriodList.reduce<Record<string, PreferenceInputsType[]>>((acc, dto) => {
        const dateKey: string = dto.date;

        const currentPreferences = acc[dateKey];
        const newPreferences = !currentPreferences ? [dto] : [...currentPreferences, dto];

        return { ...acc, [dateKey]: newPreferences };
      }, {});

      return { ...acc, [periodKey]: preferencesByDate };
    },
    {} as Record<string, Record<string, PreferenceInputsType[]>>,
  );

  return {
    id,
    employeeId,
    cycleStart,
    validFrom,
    cycleLength,
    type,
    groups: preferencesByPeriodAndDate,
    isLocked,
  };
};

export const getAvailabilityDateText = (
  type: CycleTypeEnums,
  periodIndex: number,
  dayOfCycle: number,
  date: string,
  t: TranslationFunction,
) => {
  const day = moment(date, API_DATE_FORMAT).format(DAY_FULL_FORMAT);

  switch (type) {
    case CycleTypeEnums.DAYS:
      return `${t('availability.day')} ${dayOfCycle}`;
    case CycleTypeEnums.WEEKLY:
      return day;
    case CycleTypeEnums.BI_WEEKLY:
      return `${t('availability.week')} ${periodIndex}, ${day}`;
    default:
    // No default
  }
};

export const getAvailabilityTitle = (
  startTimeInSeconds: number | null,
  endTimeInSeconds: number | null,
  timeFormat: string | undefined,
  t: TranslationFunction,
): string => {
  return isNil(startTimeInSeconds) && isNil(endTimeInSeconds)
    ? t('availability.allDay')
    : `${getTimeFromSeconds(startTimeInSeconds, timeFormat)} - ${getTimeFromSeconds(endTimeInSeconds, timeFormat)}`;
};

export const getStatusColor = (theme: Theme, status: AvailabilityStatusEnums): string => {
  return status === AvailabilityStatusEnums.NOT_AVAILABLE ? theme.palette.error.main : 'auto';
};
